/**
 * 事件上报工具
 * 用于页面访问量和按钮点击事件的统计上报
 */

import { isMobile, getSecondLevelDomain } from './index.js'
import cache from './cache/index.js'
import session from './session.js'
import Cookies from 'js-cookie'

const EVENT_SOURCE = 'website'
const EVENT_TRACKER_UNIQUE_ID = '__tracker_log_uid__'
// 动态获取二级域名，例如: luckyyx.com、meowplayer.com 等
const EVENT_SEND_URL = `https://log.${getSecondLevelDomain()}.com/webpage/event/log`
// const EVENT_SEND_URL = `https://log.luckyyx.com/webpage/event/log`

// 事件类型常量
const EVENT_TYPES = {
	PAGE_VIEW: 'PageView',
	BUY_BUTTON_CLICK: 'BuyButtonClick',
	PRODUCT_CLICK: 'ProductClick',
	ORDER_CREATED: 'OrderCreated',
	PAGE_UNLOAD: 'PageUnload'
}

// 页面类型常量
const PAGE_TYPES = {
	SHOP: 'shop',
	ORDER: 'order',
	HOME: 'home',
	WELFARE: 'welfare',
	POINTS: 'points'
}

class EventTracker {
	constructor() {
		this.init()
	}

	/**
	 * 初始化事件上报，生成唯一用户ID
	 */
	init() {
		let uniqueId = Cookies.get(EVENT_TRACKER_UNIQUE_ID)
		if (!uniqueId) {
			uniqueId = this.createUniqueId()
			 // 保存1年
			Cookies.set(EVENT_TRACKER_UNIQUE_ID, uniqueId, { expires: 365 })
		}
	}

	/**
	 * 生成唯一标识符
	 * 使用时间戳 + 随机数组合，确保唯一性
	 */
	createUniqueId() {
		const timestamp = Date.now().toString(36)
		const randomPart = Math.random().toString(36).substring(2, 15)
		const randomPart2 = Math.random().toString(36).substring(2, 15)
		return `${timestamp}-${randomPart}-${randomPart2}`
	}

	/**
	 * 获取URL参数
	 */
	getQueryParam(name) {
		const urlParams = new URLSearchParams(window.location.search)
		return urlParams.get(name)
	}

	/**
	 * 获取设备类型
	 */
	getDeviceType() {
		return isMobile() ? 'MB' : 'PC'
	}

	/**
	 * 获取操作系统
	 * 从 User Agent 中解析操作系统类型
	 */
	getOS() {
		const ua = navigator.userAgent.toLowerCase()
		const platform = navigator.platform.toLowerCase()
		
		// 移动端优先检测
		if (/android/.test(ua)) return 'Android'
		if (/iphone|ipad|ipod/.test(ua)) return 'iOS'
		
		// 桌面端检测
		if (/win/.test(platform)) return 'Windows'
		if (/mac/.test(platform)) return 'MacOS'
		if (/linux/.test(platform)) return 'Linux'
		
		return 'Other'
	}

	/**
	 * 获取渠道标识
	 */
	getMediaSource() {
		const hostname = window.location.hostname
		const subdomain = hostname.split('.')[0]
		const segments = subdomain.split('-')
		
		// wx-{gameCode}-{channel} 格式
		if (segments.length === 3) {
			return segments[2]
		}
		
		// 默认返回 web
		return 'web'
	}

	/**
	 * 发送事件上报
	 * @param {string} eventName - 事件名称 (如: 'page_view', 'button_click')
	 * @param {string} eventSource - 事件来源 (如: 'home', 'shop', 'orders')
	 * @param {string} pageType - 页面类型 (如: 'index', 'detail')
	 * @param {object} extraData - 额外数据
	 */
	async track(eventName, extraData = {}) {
		try {
			const uniqueId = Cookies.get(EVENT_TRACKER_UNIQUE_ID) || this.createUniqueId()
			const gameCode = cache.getGameCode()
			const mediaSource = this.getMediaSource()

			const eventData = {
				gc: gameCode,                 // 游戏代码
				es: EVENT_SOURCE,             // 事件来源
				ms: mediaSource,              // 媒体来源
				cId: '',                      // 广告系列ID
				asId: '',                     // 广告组ID
				adId: '',                     // 广告ID
				en: eventName,                // 事件名称
				et: new Date().getTime(),     // 事件时间
				uid: uniqueId,                // 设备ID
				os: this.getOS(),             // 操作系统
				dv: this.getDeviceType(),     // 设备类型
				py: 0,                        // 页面类型
				url: window.location.href,    // 当前页面URL
				ref: document.referrer,       // 来源页面
				userId: session.getUserId() || '',  // 用户ID
				extra: JSON.stringify(extraData) 
			}

			// 使用JSONP方式发送（避免跨域问题）
			this.sendByJsonp(EVENT_SEND_URL, eventData)

			// 备用：使用Beacon API（页面卸载时也能发送）
			if (navigator.sendBeacon && eventName === EVENT_TYPES.PAGE_UNLOAD) {
				const formData = new FormData()
				Object.keys(eventData).forEach(key => {
					formData.append(key, eventData[key])
				})
				navigator.sendBeacon(EVENT_SEND_URL, formData)
			}
		} catch (error) {
			console.warn('Event tracking failed:', error)
		}
	}

	/**
	 * 使用JSONP发送数据
	 */
	sendByJsonp(url, data) {
		const params = new URLSearchParams(data).toString()
		const script = document.createElement('script')
		script.src = `${url}?${params}&callback=__eventLogCallback__`
		
		// 定义回调函数
		window.__eventLogCallback__ = function(response) {
			// 可选：处理响应
			script.remove()
		}
		
		// 超时处理
		setTimeout(() => {
			if (script.parentNode) {
				script.remove()
			}
		}, 5000)
		
		document.head.appendChild(script)
	}

	/**
	 * 页面访问事件
	 * @param {string} pageName - 页面名称
	 * @param {object} extraData - 额外数据
	 */
	pageView(extraData = {}) {
		this.track(EVENT_TYPES.PAGE_VIEW, extraData)
	}

	/**
	 * 商品点击事件
	 * @param {string} productId - 商品ID
	 * @param {string} productName - 商品名称
	 * @param {object} extraData - 额外数据
	 */
	productClick(productId, extraData = {}) {
		this.track(EVENT_TYPES.PRODUCT_CLICK, {
			productId,
			...extraData
		})
	}
}

// 创建单例
const eventTracker = new EventTracker()

// 导出常量，方便外部使用
export { EVENT_TYPES, PAGE_TYPES }
export default eventTracker